// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/utils/documentWorkflow.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * Document approval workflow engine — pure functions only, no side effects.
 *
 * State machine:
 *   draft → in_review → changes_requested → ready_for_approval → approved → published
 *   published → expired | obsolete
 *   any       → obsolete
 *
 * Backward compat: 'to_review' is treated as 'in_review' everywhere.
 */

// ── State constants ─────────────────────────────────────────────────────────

const DOC_STATUS = {
  DRAFT: 'draft',
  IN_REVIEW: 'in_review',
  CHANGES_REQUESTED: 'changes_requested',
  READY_FOR_APPROVAL: 'ready_for_approval',
  APPROVED: 'approved',
  PUBLISHED: 'published',
  EXPIRED: 'expired',
  OBSOLETE: 'obsolete',
};

// States inherited from the old model — normalized on read
const LEGACY_MAP = { to_review: DOC_STATUS.IN_REVIEW };

// ── Normalization ───────────────────────────────────────────────────────────

function normalizeDocStatus(status) {
  return LEGACY_MAP[status] || status || DOC_STATUS.DRAFT;
}

// DOC-B-001: the exact 403 message documents-write returns when a write's
// documentStatus disagrees with the stored row — the server's guard that a
// status transition must go through documents-workflow instead. A write
// carrying this message lost only because a documents-workflow transition
// (e.g. "Submit for Review") already advanced the row first; the transition
// itself succeeded, so the write is a stale no-op, not a real failure.
const STALE_STATUS_WRITE_MESSAGE = 'Status transitions must use the documents-workflow endpoint.';

/**
 * True when `error` is documents-write's expected, benign rejection of a
 * write that raced a documents-workflow status transition (issue #3162).
 * Callers should treat this as a no-op — the server's copy is already
 * correct — rather than surfacing it as a write failure.
 */
function isStaleStatusWriteConflict(error) {
  return error?.message === STALE_STATUS_WRITE_MESSAGE;
}

/**
 * True when `error` is documents-write rejecting a write purely because the
 * document moved on underneath it — either the stale-status rejection above,
 * or the optimistic-concurrency 409.
 *
 * Use this ONLY for a follow-up write that carries no user content of its own
 * (the version-snapshot blob ref), where the server's copy is already correct
 * and nothing is lost by dropping the write. A write that does carry user
 * content must NOT be silenced by this — it has to surface so the edit can be
 * re-applied against the server's version.
 *
 * documents-write checks the version before the status guard (#5072), so this
 * race now reports as a 409 where it previously reported as the 403 message;
 * both are accepted here so the benign case stays benign either way (#3162).
 */
function isConcurrentDocChangeConflict(error) {
  return isStaleStatusWriteConflict(error) || error?.httpStatus === 409;
}

// ── Role resolution ─────────────────────────────────────────────────────────

/**
 * Derive document-level roles for a user.
 * Falls back to legacy 'owner' and 'approverId' fields for existing docs.
 */
function getDocRoles(doc, userId) {
  if (!userId) return { isOwner: false, isWriter: false, isReviewer: false };

  const ownerId = doc.ownerId || doc.owner;
  const writerId = doc.writerId || ownerId; // legacy: owner was the writer
  const reviewers = doc.reviewers || [];

  const isOwner = userId === ownerId;
  const isWriter = userId === writerId;
  // Support old single-reviewer model via approverId
  const isNamedReviewer = reviewers.some((r) => r.userId === userId) || userId === doc.approverId;
  // DE-02 problem 2: reviewer independence. documents-workflow refuses a review
  // decision from the writer or owner however they came to be named, so the
  // submit_reviewer_decision rule below must not claim they can cast one.
  const isReviewer = isNamedReviewer && !isOwner && !isWriter;

  return { isOwner, isWriter, isReviewer };
}

// ── Quorum evaluation ───────────────────────────────────────────────────────

/**
 * True when enough reviewers have said 'ok' to allow advancing past in_review.
 * Invariant I-18: required reviewers are unconditional; count threshold is additive.
 */
function reviewersQuorumSatisfied(doc) {
  const reviewers = doc.reviewers || [];
  const reviewDecisions = currentReviewDecisions(doc);

  const required = reviewers.filter((r) => r.required === true);
  const allRequiredOK = required.every((r) =>
    reviewDecisions.some((d) => d.reviewerId === r.userId && d.decision === 'ok')
  );
  if (!allRequiredOK) return false;

  if (doc.requiredReviewerCount == null) return true;
  const okCount = reviewDecisions.filter((d) => d.decision === 'ok').length;
  return okCount >= doc.requiredReviewerCount;
}

/**
 * A stable, cheap fingerprint of the substance a reviewer actually read — the
 * title and the block content. FNV-1a, run twice with different offset bases
 * and concatenated, so it needs no crypto API and produces the same string in
 * the browser and in the Azure Function.
 *
 * Mirrored byte-for-byte in azure-functions/documents-workflow/index.js
 * (documentContentFingerprint). The server is the authority; this copy exists
 * only so the UI can show the same verdict before the request is sent.
 */
function documentContentFingerprint(doc) {
  const payload = JSON.stringify([doc?.title || '', doc?.content || []]);
  const fnv = (offset) => {
    let h = offset;
    for (let i = 0; i < payload.length; i += 1) {
      h ^= payload.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return fnv(0x811c9dc5) + fnv(0x7fffffff);
}

/**
 * The review decisions that are about the text as it stands now (#6964,
 * DOC-B-010). A decision whose fingerprint no longer matches — or that predates
 * fingerprints and carries none — stays on record but no longer counts, and
 * that reviewer may decide again. Mirrors currentReviewDecisions in
 * azure-functions/documents-workflow/index.js.
 */
function currentReviewDecisions(doc) {
  const fingerprint = documentContentFingerprint(doc);
  return (doc?.reviewDecisions || []).filter((d) => d && d.reviewedFingerprint === fingerprint);
}

/**
 * The integrity hash an approval binds to (#6964, DOC-B-010): SHA-256 over the
 * same [title, content] payload as documentContentFingerprint, tagged with its
 * algorithm. The approver's browser sends the hash of the document it showed;
 * documents-workflow recomputes it from the stored body and refuses the
 * approval when they differ (ERR_CONTENT_CHANGED). Mirrors documentContentHash
 * in azure-functions/documents-workflow/index.js.
 *
 * @param {{ title?: string, content?: any[] } | null | undefined} doc
 * @returns {Promise<string>}
 */
async function documentContentHash(doc) {
  const payload = JSON.stringify([doc?.title || '', doc?.content || []]);
  const bytes = new TextEncoder().encode(payload);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return `sha256:${hex}`;
}

/**
 * The 'ok' decisions on the current review cycle that were cast by someone who
 * is neither the document's writer nor its owner, ON the document as it stands
 * now — i.e. the record of an independent check of THIS text.
 *
 * Review decisions are cleared whenever the cycle restarts (submit_for_review,
 * retract, resubmit, start_new_version, and a mid-review owner reassignment),
 * so anything still on record belongs to the current cycle. Two further
 * conditions are re-derived here rather than trusted from the point the
 * decision was cast:
 *
 *  - Independence, from the decision's own reviewerId, so a later role change
 *    can never leave a stale decision standing in for an independent reviewer.
 *  - The artefact, from the fingerprint the decision carries. documents-write
 *    now freezes the body outside draft / changes_requested (#6671), so the
 *    text cannot move under a live review; the check remains as defence in
 *    depth and for decisions recorded before that freeze. A decision whose
 *    fingerprint no longer matches is about text that is no longer there. Decisions
 *    recorded before fingerprinting existed carry none and never count — that
 *    leaves them exactly where they are today (the flagged override path),
 *    which is the fail-safe direction.
 */
function independentReviewerApprovals(doc) {
  const ownerId = doc.ownerId || doc.owner;
  const writerId = doc.writerId || ownerId;
  const fingerprint = documentContentFingerprint(doc);
  return (doc.reviewDecisions || []).filter(
    (d) =>
      d.decision === 'ok' &&
      d.reviewerId &&
      d.reviewerId !== ownerId &&
      d.reviewerId !== writerId &&
      d.reviewedFingerprint === fingerprint
  );
}

/**
 * True when segregation of duties has been satisfied by an independent review
 * rather than by the writer and approver being different people.
 *
 * DOC-B-007: I-17 asks whether the document had an independent check before
 * approval, and a completed review cycle is that check — the reviewer is
 * required to be neither writer nor owner, and the server refuses their
 * decision otherwise. Quorum is required as well as a single independent 'ok',
 * so a writer's reviewer-override (which advances a cycle the reviewers did
 * NOT finish) never counts, and neither does a document submitted with no
 * reviewers at all.
 */
function independentReviewSatisfied(doc) {
  return reviewersQuorumSatisfied(doc) && independentReviewerApprovals(doc).length > 0;
}

/**
 * True when a reviewer has NOT yet submitted a decision about the current text.
 * Invariant I-12, refined by #6964: one CURRENT decision per reviewer per
 * cycle — a reviewer whose only decision is about other text may decide again.
 */
function reviewerCanDecide(doc, userId) {
  return !currentReviewDecisions(doc).some((d) => d.reviewerId === userId);
}

// ── Transition validation ───────────────────────────────────────────────────

const TRANSITION_RULES = {
  submit_for_review: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.DRAFT && isWriter;
  },

  submit_reviewer_decision: (doc, userId) => {
    const { isReviewer } = getDocRoles(doc, userId);
    return (
      normalizeDocStatus(doc.documentStatus) === DOC_STATUS.IN_REVIEW &&
      isReviewer &&
      reviewerCanDecide(doc, userId)
    );
  },

  advance_to_ready: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    const status = normalizeDocStatus(doc.documentStatus);
    if (status !== DOC_STATUS.IN_REVIEW || !isWriter) return false;
    return reviewersQuorumSatisfied(doc) || (doc.reviewers || []).length === 0;
  },

  override_reviewers: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.IN_REVIEW && isWriter;
  },

  retract: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.IN_REVIEW && isWriter;
  },

  resubmit_after_changes: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.CHANGES_REQUESTED && isWriter;
  },

  approve: (doc, userId) => {
    const { isOwner, isWriter } = getDocRoles(doc, userId);
    // Invariant I-17: runtime guard — reject if owner is also writer, UNLESS an
    // independent reviewer already signed the current cycle off. I-17 exists to
    // guarantee a second pair of eyes before approval, and a completed review
    // cycle is that second pair of eyes: the reviewer can be neither the writer
    // nor the owner. Without this, a document that HAD an independent review was
    // still pushed down the DOC-B-007 self-override path and its approval
    // branded a self-approval in the audit trail.
    if (isOwner && isWriter && !independentReviewSatisfied(doc)) return false;
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.READY_FOR_APPROVAL && isOwner;
  },

  reject_by_owner: (doc, userId) => {
    const { isOwner } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.READY_FOR_APPROVAL && isOwner;
  },

  publish: (doc, userId) => {
    const { isOwner, isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.APPROVED && (isWriter || isOwner);
  },

  publish_direct: (doc, userId) => {
    // approvalRequired === false path (DE-02 / DOC-B-005): a direct publish is
    // an admin/owner authorization, never a writer self-serve. This rule has no
    // caller-role context, so it can only vouch for the owner (who must not also
    // be the writer, per I-17); admin callers are gated at the call site where
    // the session role is known (useDocumentState.publishDocumentDirect).
    if (doc.approvalRequired !== false) return false;
    const { isOwner, isWriter } = getDocRoles(doc, userId);
    const status = normalizeDocStatus(doc.documentStatus);
    return (status === DOC_STATUS.DRAFT || status === DOC_STATUS.IN_REVIEW) && isOwner && !isWriter;
  },

  start_new_version: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.PUBLISHED && isWriter;
  },

  expire_manual: (doc, userId) => {
    const { isOwner, isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.PUBLISHED && (isOwner || isWriter);
  },

  obsolete: (_doc, _userId) => true, // role guard (admin/owner) enforced by caller

  apply_minor_amendment: (doc, userId) => {
    const { isWriter } = getDocRoles(doc, userId);
    return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.PUBLISHED && isWriter;
  },

  reassign_owner: (_doc, _userId) => true, // admin-only; enforced by caller
};

function canTransition(doc, action, userId) {
  const rule = TRANSITION_RULES[action];
  return rule ? rule(doc, userId) : false;
}

// Statuses documents-delete always refuses with ERR_WRONG_STATE (DOC-B-003) —
// the document is mid-approval or live and must be moved to obsolete/expired
// via documents-workflow first. Mirrors BLOCKED_STATUSES in
// azure-functions/documents-delete/index.js so the UI never offers an action
// the server will always reject.
const DELETE_BLOCKED_STATUSES = new Set([
  DOC_STATUS.IN_REVIEW,
  DOC_STATUS.READY_FOR_APPROVAL,
  DOC_STATUS.APPROVED,
  DOC_STATUS.PUBLISHED,
]);

/** True when documents-delete will accept a direct soft-delete of this document. */
function canDeleteDirectly(doc) {
  return !DELETE_BLOCKED_STATUSES.has(normalizeDocStatus(doc?.documentStatus));
}

/**
 * True when this session may mark the document obsolete via documents-workflow's
 * `obsolete` action — mirrors the server-side gate (Owner | admin/super-admin) so
 * the UI never offers a control that will always 403.
 */
function canMarkObsolete(doc, session) {
  if (!session) return false;
  if (session.role === 'admin' || session.role === 'super-admin') return true;
  const ownerId = doc?.ownerId || doc?.owner;
  return !!ownerId && ownerId === session.userId;
}

/**
 * SUP-07 (GH #7123): may this session record a periodic review that found no
 * change? Mirrors documents-workflow's record_review (the authority): the
 * document is in force, no revision is open beside it, and the caller is its
 * owner, its writer or an admin.
 */
function canRecordReview(doc, session) {
  if (!session || !doc) return false;
  if ((doc.documentStatus || 'draft') !== 'published' || doc.draftRevision) return false;
  if (session.role === 'admin' || session.role === 'super-admin') return true;
  const ownerId = doc.ownerId || doc.owner;
  const writerId = doc.writerId || ownerId;
  return !!session.userId && (session.userId === ownerId || session.userId === writerId);
}

// ── Version helpers ─────────────────────────────────────────────────────────

/** Bump minor component: "2.3" → "2.4" */
function bumpMinor(version) {
  const parts = String(version || '0.0').split('.');
  const major = parseInt(parts[0], 10) || 0;
  const minor = parseInt(parts[1], 10) || 0;
  return `${major}.${minor + 1}`;
}

/** Bump to next major: "2.5" → "3.0" */
function bumpMajor(version) {
  const parts = String(version || '0.0').split('.');
  const major = parseInt(parts[0], 10) || 0;
  return `${major + 1}.0`;
}

// ── State history helpers ───────────────────────────────────────────────────

/** Build an immutable state history entry. */
function makeHistoryEntry({ from, to, by, comment = null, reason = null, extra = {} }) {
  return {
    from,
    to,
    at: new Date().toISOString(),
    by,
    comment: comment || null,
    reason: reason || null,
    ...extra,
  };
}

// ── Display helpers ─────────────────────────────────────────────────────────

/**
 * User-facing labels for the three document roles. The stored field names
 * (`writerId`, `ownerId`) are unchanged — this is a presentation-only mapping so
 * the UI matches user intuition: the person who authors/maintains a document is
 * its "Document owner"; the person who formally signs it off is the "Approver".
 * Always render roles through these labels; never surface the raw field names.
 */
const DOC_ROLE_LABELS = {
  writer: 'Document owner', // doc.writerId
  owner: 'Approver', // doc.ownerId
  reviewer: 'Reviewer', // doc.reviewers[]
};

const DOC_STATUS_CFG = {
  [DOC_STATUS.DRAFT]: {
    label: 'Draft',
    bg: 'rgba(251,146,60,0.12)',
    color: '#fb923c',
    border: 'rgba(251,146,60,0.3)',
  },
  [DOC_STATUS.IN_REVIEW]: {
    label: 'In Review',
    bg: 'rgba(167,139,250,0.12)',
    color: '#a78bfa',
    border: 'rgba(167,139,250,0.3)',
  },
  [DOC_STATUS.CHANGES_REQUESTED]: {
    label: 'Changes Requested',
    bg: 'rgba(251,191,36,0.12)',
    color: '#fbbf24',
    border: 'rgba(251,191,36,0.3)',
  },
  [DOC_STATUS.READY_FOR_APPROVAL]: {
    label: 'Ready for Approval',
    bg: 'rgba(96,165,250,0.12)',
    color: '#60a5fa',
    border: 'rgba(96,165,250,0.3)',
  },
  [DOC_STATUS.APPROVED]: {
    label: 'Approved',
    bg: 'rgba(74,222,128,0.12)',
    color: '#4ade80',
    border: 'rgba(74,222,128,0.3)',
  },
  [DOC_STATUS.PUBLISHED]: {
    label: 'Published',
    bg: 'rgba(94,234,212,0.1)',
    color: 'var(--accent)',
    border: 'rgba(94,234,212,0.25)',
  },
  [DOC_STATUS.EXPIRED]: {
    label: 'Expired',
    bg: 'rgba(251,113,133,0.1)',
    color: '#fb7185',
    border: 'rgba(251,113,133,0.25)',
  },
  [DOC_STATUS.OBSOLETE]: {
    label: 'Obsolete',
    bg: 'rgba(113,113,122,0.1)',
    color: '#71717a',
    border: 'rgba(113,113,122,0.25)',
  },
  // Legacy alias
  to_review: {
    label: 'In Review',
    bg: 'rgba(167,139,250,0.12)',
    color: '#a78bfa',
    border: 'rgba(167,139,250,0.3)',
  },
};

/** Pending-approval states that surface in Action Centre for the Owner. */
function isAwaitingOwnerAction(doc) {
  return normalizeDocStatus(doc.documentStatus) === DOC_STATUS.READY_FOR_APPROVAL;
}

/** Pending-review states that surface in Action Centre for a Reviewer. */
function isAwaitingReviewerAction(doc, userId) {
  if (normalizeDocStatus(doc.documentStatus) !== DOC_STATUS.IN_REVIEW) return false;
  // DE-02 problem 2: the writer or owner can never cast a review decision, so
  // listing them as a reviewer must not raise an Action Centre item they have
  // no way to clear. Narrow guard only — this never adds a badge, it only
  // withholds one that could never be actioned.
  const { isOwner, isWriter } = getDocRoles(doc, userId);
  if (isOwner || isWriter) return false;
  return (doc.reviewers || []).some((r) => r.userId === userId) && reviewerCanDecide(doc, userId);
}

/**
 * Pending-publish state that surfaces in Action Centre for the person who can
 * publish. A document approved by the owner sits at 'approved' until the writer
 * or owner runs the final publish step — until then it never satisfies a control
 * gate. Mirrors the `publish` transition rule (writer or owner) so the Action
 * Centre and the actual permission can never disagree.
 */
function isAwaitingPublish(doc, userId) {
  if (normalizeDocStatus(doc.documentStatus) !== DOC_STATUS.APPROVED) return false;
  const { isOwner, isWriter } = getDocRoles(doc, userId);
  return isOwner || isWriter;
}

module.exports = {
  DOC_STATUS,
  normalizeDocStatus,
  isStaleStatusWriteConflict,
  isConcurrentDocChangeConflict,
  getDocRoles,
  reviewersQuorumSatisfied,
  documentContentFingerprint,
  currentReviewDecisions,
  documentContentHash,
  independentReviewerApprovals,
  independentReviewSatisfied,
  reviewerCanDecide,
  canTransition,
  canDeleteDirectly,
  canMarkObsolete,
  canRecordReview,
  bumpMinor,
  bumpMajor,
  makeHistoryEntry,
  DOC_ROLE_LABELS,
  DOC_STATUS_CFG,
  isAwaitingOwnerAction,
  isAwaitingReviewerAction,
  isAwaitingPublish,
};
